import { useCallback, useMemo } from 'react';

import { useStreamFunctions } from 'stream/globalFunction/useStreamFunctions';

import type { CreateFunctionCommand as CreateFunctionPayload } from '@polyai/schemas';
import { flowFunctionsSelectors } from '@polyai/stream/store/reducers/flows';
import { createFunction } from '@polyai/stream/store/reducers/functions';

import { generateDefaultFunctionCode } from 'components/molecules/RichTextInput/utils';
import { useServerDispatch } from 'components/organisms/AgentStream/context/AgentStreamContext';
import { getUniqueName } from 'hooks/useFlows/useFlows';
import { generateFunctionId } from 'lib/prettyUuid';
import useFlowState from 'screens/FlowDetails/hooks/useFlowState';
import { sanitiseFunctionName } from 'utils/functions';

const useCreateFlowFunction = () => {
  const { flow } = useFlowState();
  const { activeFunctions } = useStreamFunctions();
  const serverDispatch = useServerDispatch();

  const funcNames = useMemo(
    () =>
      [
        ...activeFunctions,
        ...(flow?.transitionFunctions
          ? flowFunctionsSelectors
              .selectAll(flow.transitionFunctions)
              .filter(func => !func.archived)
          : []),
      ].map(f => f.name),
    [activeFunctions, flow?.transitionFunctions],
  );

  const handleAddFlowFunction = useCallback(
    (flowName: string) => {
      const funcName = getUniqueName(
        funcNames,
        `start_${sanitiseFunctionName(flowName)}`,
        '_',
      );

      const payload: CreateFunctionPayload = {
        id: generateFunctionId(),
        name: funcName,
        description: `Enter the ${flowName} flow`,
        parameters: [],
        code: generateDefaultFunctionCode({
          functionName: funcName,
          code: `  conv.goto_flow("${flowName}")`,
        }),
        latencyControl: {
          initialDelay: 0,
          interval: 0,
          delayResponses: [],
          enabled: false,
        },
        archived: false,
        errors: [],
        references: {
          behaviour: {},
          flowSteps: {},
          topics: {},
          stopKeywords: {},
          variables: {},
        },
      };

      void serverDispatch(createFunction(payload));

      return payload;
    },
    [funcNames, serverDispatch],
  );

  return [handleAddFlowFunction];
};

export default useCreateFlowFunction;
